import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import platformService from "../services/platformService";
import { Users, Building2, Layers, Activity, LogIn } from "lucide-react";
import { parseApiDateTime } from "../utils/datetime";

const ACTION_LABELS = {
  login: "Login",
  create: "Criação",
  update: "Edição",
  delete: "Exclusão",
  comment: "Comentário",
};

const fmtDateTime = (v) => {
  const d = parseApiDateTime(v);
  if (!d) return "";
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Gráfico de barras verticais (atividade 7 dias) — leve, sem dependência.
const BarChart7d = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end justify-between gap-2 h-44 pt-2">
      {data.map((d) => (
        <div key={d.date} className="flex flex-1 flex-col items-center gap-1.5">
          <div className="flex w-full flex-1 items-end justify-center">
            <div
              className="w-7 rounded-t-md bg-gradient-to-t from-blue-600 to-blue-400 transition-all"
              style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? 4 : 0 }}
              title={`${d.count} evento(s)`}
            />
          </div>
          <span className="text-xs font-semibold text-gray-700">{d.count}</span>
          <span className="text-[11px] text-gray-400">{d.label}</span>
        </div>
      ))}
    </div>
  );
};

// Barras horizontais (colaboradores por departamento).
const DeptBars = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  if (!data.length) {
    return <p className="text-sm text-gray-500 py-6 text-center">Sem colaboradores cadastrados.</p>;
  }
  return (
    <div className="space-y-3 pt-2">
      {data.map((d) => (
        <div key={d.department}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="truncate text-gray-700">{d.department}</span>
            <span className="font-semibold text-gray-900 ml-2">{d.count}</span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-700"
              style={{ width: `${(d.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const SaudePlataforma = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const activeTenantName = useMemo(
    () => localStorage.getItem("active_tenant_name") || "",
    []
  );

  useEffect(() => {
    let alive = true;
    platformService
      .health()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((err) => {
        if (alive) setError(err.response?.data?.detail || "Erro ao carregar a Saúde da Plataforma.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const cards = data?.cards || {};
  const statCards = [
    { title: "Usuários ativos", value: cards.active_users, icon: Users, color: "text-blue-600", bg: "bg-blue-100" },
    { title: "Departamentos", value: cards.departments, icon: Building2, color: "text-indigo-600", bg: "bg-indigo-100" },
    { title: "Telas cadastradas", value: cards.screens, icon: Layers, color: "text-violet-600", bg: "bg-violet-100" },
    { title: "Eventos 24h", value: cards.events_24h, icon: Activity, color: "text-emerald-600", bg: "bg-emerald-100" },
    { title: "Logins 7 dias", value: cards.logins_7d, icon: LogIn, color: "text-amber-600", bg: "bg-amber-100" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saúde da Plataforma</h1>
          <p className="text-gray-500">
            Visão geral operacional{activeTenantName ? ` — ${activeTenantName}` : ""}.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>
        ) : (
          <>
            {/* Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {statCards.map((s) => {
                const Icon = s.icon;
                return (
                  <Card key={s.title}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-600 truncate">{s.title}</p>
                          <p className="text-3xl font-bold text-gray-900 mt-2">{s.value ?? 0}</p>
                        </div>
                        <div className={`p-3 rounded-full ${s.bg} shrink-0`}>
                          <Icon className={`w-5 h-5 ${s.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    Atividade — últimos 7 dias
                  </h2>
                  <BarChart7d data={data?.activity_7d || []} />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    Colaboradores por departamento
                  </h2>
                  <DeptBars data={data?.users_by_department || []} />
                </CardContent>
              </Card>
            </div>

            {/* Logins + Atividade recente */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Últimos logins</h2>
                  {(data?.recent_logins || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum login registrado ainda.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {data.recent_logins.map((l, i) => (
                        <li key={i} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-gray-800">
                            <LogIn size={14} className="text-amber-500" />
                            {l.user_name || "—"}
                          </span>
                          <span className="text-gray-400">{fmtDateTime(l.at)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Atividade recente (auditoria)
                  </h2>
                  {(data?.recent_activity || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum evento registrado ainda.</p>
                  ) : (
                    <ul className="space-y-3">
                      {data.recent_activity.map((a, i) => (
                        <li key={i} className="text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-gray-900 truncate">
                              {a.user_name || "—"}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {fmtDateTime(a.at)}
                            </span>
                          </div>
                          <p className="text-gray-600 truncate">
                            <span className="text-gray-400">
                              {ACTION_LABELS[a.action] || a.action}:
                            </span>{" "}
                            {a.description || a.entity}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SaudePlataforma;
